'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import ToolSEO from '@/components/ToolSEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import SocialShareButtons from '@/components/SocialShareButtons';
import UniversalBookmark from '@/components/UniversalBookmark';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { saveToolScore } from '@/lib/tool-score-storage';

interface TechDebtItem {
  id: string;
  title: string;
  description: string;
  impact: number; // 1-5 (user experience, system stability, team velocity)
  effort: number; // 1-5 (time and resources required)
  risk: number; // 1-5 (risk if not addressed)
  urgency: number; // 1-5 (time sensitivity)
  category: 'architecture' | 'code-quality' | 'performance' | 'security' | 'documentation' | 'testing' | 'infrastructure';
}

interface PrioritizedItem extends TechDebtItem {
  score: number;
  priorityLevel: 'critical' | 'high' | 'medium' | 'low';
  recommendation: string;
  roi: number; // Return on investment score
}

export default function TechDebtPrioritizerPage() {
  const [items, setItems] = useState<TechDebtItem[]>([
    {
      id: '1',
      title: 'Example: Refactor authentication service',
      description: 'Current auth service is monolithic and hard to maintain',
      impact: 4,
      effort: 3,
      risk: 4,
      urgency: 3,
      category: 'architecture',
    },
  ]);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'tech-debt-prioritizer',
    toolCategory: 'analyzer',
  });

  const [newItem, setNewItem] = useState<Partial<TechDebtItem>>({
    title: '',
    description: '',
    impact: 3,
    effort: 3,
    risk: 3,
    urgency: 3,
    category: 'code-quality',
  });

  const addItem = () => {
    if (newItem.title?.trim()) {
      const item: TechDebtItem = {
        id: Date.now().toString(),
        title: newItem.title.trim(),
        description: newItem.description || '',
        impact: newItem.impact || 3,
        effort: newItem.effort || 3,
        risk: newItem.risk || 3,
        urgency: newItem.urgency || 3,
        category: newItem.category || 'code-quality',
      };
      setItems([...items, item]);
      setNewItem({
        title: '',
        description: '',
        impact: 3,
        effort: 3,
        risk: 3,
        urgency: 3,
        category: 'code-quality',
      });
    }
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, updates: Partial<TechDebtItem>) => {
    setItems(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  // Calculate priority score and recommendations
  const prioritizedItems: PrioritizedItem[] = items.map(item => {
    // Priority score calculation
    // Impact and Risk are weighted higher (3x), Urgency medium (2x), Effort is inverse (divide by it)
    const score = ((item.impact * 3) + (item.risk * 3) + (item.urgency * 2)) / item.effort;

    // ROI calculation (benefit / cost)
    const roi = (item.impact + item.risk) / item.effort;

    // Determine priority level based on score
    let priorityLevel: PrioritizedItem['priorityLevel'];
    if (score >= 7) priorityLevel = 'critical';
    else if (score >= 5) priorityLevel = 'high';
    else if (score >= 3) priorityLevel = 'medium';
    else priorityLevel = 'low';

    // Generate recommendation
    let recommendation = '';
    if (priorityLevel === 'critical') {
      recommendation = 'Address immediately - high impact/risk with manageable effort';
    } else if (priorityLevel === 'high') {
      if (item.effort >= 4) {
        recommendation = 'Plan for next quarter - high value but significant effort required';
      } else {
        recommendation = 'Schedule within next sprint - good ROI and moderate effort';
      }
    } else if (priorityLevel === 'medium') {
      if (item.impact <= 2) {
        recommendation = 'Defer - low impact doesn\'t justify effort';
      } else {
        recommendation = 'Backlog candidate - address when capacity allows';
      }
    } else {
      recommendation = 'Low priority - consider if opportunity cost is minimal';
    }

    // Special cases
    if (item.risk >= 4 && item.category === 'security') {
      priorityLevel = 'critical';
      recommendation = 'Security risk - address immediately regardless of effort';
    }

    if (item.urgency >= 4 && item.impact >= 4) {
      priorityLevel = 'critical';
      recommendation = 'Time-sensitive high-impact item - prioritize now';
    }

    return {
      ...item,
      score,
      priorityLevel,
      recommendation,
      roi,
    };
  }).sort((a, b) => b.score - a.score);

  useEffect(() => {
    if (prioritizedItems.length > 0) {
      const cc = prioritizedItems.filter(i => i.priorityLevel === 'critical').length;
      const hc = prioritizedItems.filter(i => i.priorityLevel === 'high').length;
      saveToolScore('tech-debt-prioritizer', Math.max(0, 100 - (cc * 20) - (hc * 10)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const categoryEmojis = {
    'architecture': '🏗️',
    'code-quality': '✨',
    'performance': '⚡',
    'security': '🔐',
    'documentation': '📚',
    'testing': '🧪',
    'infrastructure': '🔧',
  };

  const priorityColors = {
    'critical': 'border-red-500 bg-red-500/10',
    'high': 'border-accent bg-accent-muted',
    'medium': 'border-yellow-500 bg-yellow-500/10',
    'low': 'border-border bg-background/50',
  };

  const priorityLabels = {
    'critical': '🔴 Critical',
    'high': '🟠 High',
    'medium': '🟡 Medium',
    'low': '⚪ Low',
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled || prioritizedItems.length === 0) return;

    const gaps = prioritizedItems
      .filter(item => item.priorityLevel === 'critical' || item.priorityLevel === 'high')
      .map(item => ({
        id: item.id,
        title: item.title,
        severity: item.priorityLevel === 'critical' ? 'critical' as const : 'high' as const,
        description: `${item.category} - Score: ${item.score.toFixed(1)}, ROI: ${item.roi.toFixed(1)}`,
      }));

    const recs = prioritizedItems.slice(0, 5).map(item => ({
      id: item.id,
      title: `Address: ${item.title}`,
      priority: item.priorityLevel === 'critical' ? 'high' as const :
                item.priorityLevel === 'high' ? 'high' as const : 'medium' as const,
      description: item.recommendation,
    }));

    const categoryScores: Record<string, number> = {};
    const categories = ['architecture', 'code-quality', 'performance', 'security', 'documentation', 'testing', 'infrastructure'];
    categories.forEach(cat => {
      const catItems = prioritizedItems.filter(i => i.category === cat);
      if (catItems.length > 0) {
        const avgScore = catItems.reduce((sum, i) => sum + i.score, 0) / catItems.length;
        categoryScores[cat] = Math.min(100, avgScore * 10);
      }
    });

    const criticalCount = prioritizedItems.filter(i => i.priorityLevel === 'critical').length;
    const highCount = prioritizedItems.filter(i => i.priorityLevel === 'high').length;
    const overallScore = Math.max(0, 100 - (criticalCount * 20) - (highCount * 10));

    const assessment = await saveResult({
      name: `Tech Debt Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore,
      scores: categoryScores,
      answers: prioritizedItems.reduce((acc, item) => ({
        ...acc,
        [item.id]: { impact: item.impact, effort: item.effort, risk: item.risk, urgency: item.urgency }
      }), {}),
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const TOOL_NAME = 'Tech Debt Prioritizer';
  const TOOL_SLUG = 'tech-debt-prioritizer';
  const TOOL_DESCRIPTION = 'Score and prioritize technical debt items based on impact, effort, and risk';

  return (
    <>
      <ToolSEO
        toolName={TOOL_NAME}
        toolSlug={TOOL_SLUG}
        description={TOOL_DESCRIPTION}
        category="analyzer"
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { name: 'Tools', url: '/tools' },
              { name: TOOL_NAME, url: `/tools/${TOOL_SLUG}` },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">📊</span>
                  <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                    {TOOL_NAME}
                  </h1>
                </div>
                <p className="text-xl text-foreground-secondary">
                  {TOOL_DESCRIPTION}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <UniversalBookmark
                  contentType="tool"
                  contentSlug={TOOL_SLUG}
                  contentTitle={TOOL_NAME}
                  variant="icon"
                />
                <SocialShareButtons
                  url={`https://theartofcto.com/tools/${TOOL_SLUG}`}
                  title={TOOL_NAME}
                  description={TOOL_DESCRIPTION}
                  variant="dropdown"
                />
              </div>
            </div>
          </div>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this prioritizer</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Add technical debt items with descriptions</li>
            <li>Rate each item on impact, effort, risk, and urgency (1-5 scale)</li>
            <li>Review the automatically calculated priority scores and ROI</li>
            <li>Follow the recommendations to sequence your work</li>
          </ol>
        </div>

        {/* Add New Item Form */}
        <div className="bg-surface rounded-lg border border-border p-6 mb-8">
          <h3 className="text-xl font-bold text-foreground mb-4">Add Tech Debt Item</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Title *
              </label>
              <input
                type="text"
                placeholder="e.g., Refactor payment processing module"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Description
              </label>
              <textarea
                placeholder="What needs to be done and why..."
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Category
                </label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value as TechDebtItem['category'] })}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="architecture">Architecture</option>
                  <option value="code-quality">Code Quality</option>
                  <option value="performance">Performance</option>
                  <option value="security">Security</option>
                  <option value="documentation">Documentation</option>
                  <option value="testing">Testing</option>
                  <option value="infrastructure">Infrastructure</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Impact: {newItem.impact}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={newItem.impact}
                  onChange={(e) => setNewItem({ ...newItem, impact: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-foreground-tertiary mt-1">User/team impact</div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Effort: {newItem.effort}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={newItem.effort}
                  onChange={(e) => setNewItem({ ...newItem, effort: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-foreground-tertiary mt-1">Time & resources</div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Risk: {newItem.risk}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={newItem.risk}
                  onChange={(e) => setNewItem({ ...newItem, risk: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-foreground-tertiary mt-1">If not addressed</div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Urgency: {newItem.urgency}/5
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={newItem.urgency}
                  onChange={(e) => setNewItem({ ...newItem, urgency: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-foreground-tertiary mt-1">Time sensitivity</div>
              </div>
            </div>

            <button
              onClick={addItem}
              disabled={!newItem.title?.trim()}
              className="w-full px-6 py-3 bg-accent text-accent-foreground rounded font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Item
            </button>
          </div>
        </div>

        {/* Prioritized List */}
        {prioritizedItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-foreground">Prioritized Items</h3>
              <div className="text-sm text-foreground-tertiary">
                {prioritizedItems.length} item{prioritizedItems.length !== 1 ? 's' : ''}
              </div>
            </div>

            {prioritizedItems.map((item, index) => (
              <div
                key={item.id}
                className={`bg-surface rounded-lg border ${priorityColors[item.priorityLevel]} p-6`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{categoryEmojis[item.category]}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-foreground">#{index + 1}</span>
                          <span className="text-xs px-2 py-1 bg-background rounded text-foreground-tertiary capitalize">
                            {item.category.replace('-', ' ')}
                          </span>
                          <span className="text-sm font-semibold">
                            {priorityLabels[item.priorityLevel]}
                          </span>
                        </div>
                        <h4 className="text-xl font-bold text-foreground mt-1">{item.title}</h4>
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-foreground-secondary text-sm mb-3 ml-11">{item.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="ml-4 px-3 py-2 text-accent hover:text-accent transition-colors"
                    title="Remove item"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6 ml-11">
                  {/* Scores */}
                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-3">Scores</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">Impact</div>
                        <div className="text-accent font-bold">{item.impact}/5</div>
                      </div>
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">Effort</div>
                        <div className="text-accent font-bold">{item.effort}/5</div>
                      </div>
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">Risk</div>
                        <div className="text-accent font-bold">{item.risk}/5</div>
                      </div>
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">Urgency</div>
                        <div className="text-accent font-bold">{item.urgency}/5</div>
                      </div>
                    </div>
                  </div>

                  {/* Calculated Metrics */}
                  <div>
                    <h5 className="text-sm font-semibold text-foreground mb-3">Analysis</h5>
                    <div className="space-y-3">
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">Priority Score</div>
                        <div className="text-accent font-bold text-xl">{item.score.toFixed(1)}</div>
                      </div>
                      <div className="bg-background rounded p-3">
                        <div className="text-xs text-foreground-tertiary mb-1">ROI</div>
                        <div className="text-accent font-bold text-xl">{item.roi.toFixed(1)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="mt-4 ml-11 p-4 bg-background rounded border border-border">
                  <div className="text-xs font-semibold text-accent mb-1">Recommendation</div>
                  <div className="text-foreground-secondary text-sm">{item.recommendation}</div>
                </div>

                {/* Edit Sliders */}
                <details className="mt-4 ml-11">
                  <summary className="cursor-pointer text-sm text-foreground-tertiary hover:text-foreground">
                    Adjust scores
                  </summary>
                  <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2">
                        Impact: {item.impact}/5
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={item.impact}
                        onChange={(e) => updateItem(item.id, { impact: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2">
                        Effort: {item.effort}/5
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={item.effort}
                        onChange={(e) => updateItem(item.id, { effort: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2">
                        Risk: {item.risk}/5
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={item.risk}
                        onChange={(e) => updateItem(item.id, { risk: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-2">
                        Urgency: {item.urgency}/5
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={item.urgency}
                        onChange={(e) => updateItem(item.id, { urgency: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {prioritizedItems.length > 0 && (
          <div className="mt-8 bg-accent-muted border border-border rounded-lg p-6">
            <h3 className="text-lg font-bold text-accent mb-4">Summary</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {prioritizedItems.filter(i => i.priorityLevel === 'critical').length}
                </div>
                <div className="text-sm text-foreground-secondary">Critical items</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {prioritizedItems.filter(i => i.priorityLevel === 'high').length}
                </div>
                <div className="text-sm text-foreground-secondary">High priority</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {prioritizedItems.filter(i => i.priorityLevel === 'medium').length}
                </div>
                <div className="text-sm text-foreground-secondary">Medium priority</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {prioritizedItems.filter(i => i.priorityLevel === 'low').length}
                </div>
                <div className="text-sm text-foreground-secondary">Low priority</div>
              </div>
            </div>

            {/* Save to Command Center */}
            {commandCenterEnabled && !saved && (
              <div className="mt-6 pt-4 border-t border-border">
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              </div>
            )}
            {saved && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/frameworks"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Technical Decision Frameworks →</h4>
              <p className="text-sm text-foreground-tertiary">
                Structured approaches to evaluating technical decisions
              </p>
            </Link>
            <Link
              href="/insights"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Tech Debt Insights →</h4>
              <p className="text-sm text-foreground-tertiary">
                Best practices for managing technical debt
              </p>
            </Link>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
